import { Component } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgClass } from '@angular/common';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { take } from 'rxjs/operators';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { InputComponent } from '../../../../shared/components/input/input.component';
import { IconComponent } from '../../../../shared/components/icon/icon.component';
import { ButtonTypeEnum } from '../../../../shared/components/button/types/button-type.enum';
import { ButtonSizeEnum } from '../../../../shared/components/button/types/button-size.enum';
import { ButtonRadiusEnum } from '../../../../shared/components/button/types/button-radius.enum';
import { UserService } from '../../../../shared/services/user2/user.service';
import { ModalService } from '../../../../shared/services/modal/modal.service';
import { SuccessModalComponent } from '../../../../shared/modals/success-modal/success-modal.component';
import { SpinnerComponent } from '../../../../shared/components/spinner/spinner.component';
import { SpinnerColorEnum } from '../../../../shared/components/spinner/types/spinner-color.enum';
import { SpinnerSizeEnum } from '../../../../shared/components/spinner/types/spinner-size.enum';
import { ToastService } from '../../../../shared/components/toast/service/toast.service';
import { getCrudErrorMessage } from '../../../../shared/utils/functions';

interface InviteUserForm {
  email: FormControl<string>;
}

@Component({
  selector: 'l-invite-user-modal',
  standalone: true,
  templateUrl: './invite-user-modal.component.html',
  imports: [
    ReactiveFormsModule,
    NgClass,
    ButtonComponent,
    InputComponent,
    IconComponent,
    SpinnerComponent
  ],
})
export class InviteUserModalComponent {

  constructor(
    private readonly ref: DynamicDialogRef,
    private readonly userService: UserService,
    private readonly modalService: ModalService,
    private readonly formBuilder: FormBuilder,
    private readonly toastService: ToastService,
  ) {
    this.formGroup = this.formBuilder.group<InviteUserForm>({
      email: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required, Validators.email],
      }),
    });
  }

  public isSubmitting: boolean = false;

  public formGroup!: FormGroup<InviteUserForm>;

  public readonly buttonTypeEnum: typeof ButtonTypeEnum = ButtonTypeEnum;

  public readonly buttonSizeEnum: typeof ButtonSizeEnum = ButtonSizeEnum;

  public readonly buttonRadiusEnum: typeof ButtonRadiusEnum = ButtonRadiusEnum;

  public readonly spinnerColorEnum: typeof SpinnerColorEnum = SpinnerColorEnum;
  
  public readonly spinnerSizeEnum: typeof SpinnerSizeEnum = SpinnerSizeEnum;

  public get emailControl(): FormControl {
    return this.formGroup.get('email') as FormControl;
  }

  public close(): void {
    this.ref.close();
  }

  public async submit(): Promise<void> {
    try {
      this.formGroup.markAllAsTouched();

      if (this.formGroup.invalid || this.isSubmitting) {
        return;
      }

      this.isSubmitting = true;

      const { email } = this.formGroup.getRawValue();
      await this.userService.invite(email);

      const successRef = this.modalService.open(SuccessModalComponent, {
        width: '500px',
        modal: true,
        closable: false,
        showHeader: false,
        data: {
          title: 'Convite enviado!',
          subtitle: `O usuário receberá um e-mail com as instruções para criar a conta.`,
          imageAlt: 'E-mail enviado',
          iconPath: 'assets/icons/share.svg',
        },
      });

      successRef.onClose.pipe(take(1)).subscribe(() => {
        this.ref.close({ reload: true });
      });
    } catch (error) {
      this.toastService.error(getCrudErrorMessage(error), 'Oops...');
    } finally {
      this.isSubmitting = false;
    }
  }

}
